import { cn } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import { Heading } from '@sohanscript/ui/heading';
import { Motion } from '@sohanscript/ui/motion';
import { WithTooltip } from '@sohanscript/ui/tooltip';
import type { ComponentProps } from 'react';

type PoweredByProps = ComponentProps<'div'>;

const TECH_LOGOS = [
	{
		name: 'Motion',
		icon: 'tabler:brand-framer-motion',
		url: 'https://motion.dev',
	},
	{
		name: 'PostHog',
		icon: 'logos:posthog-icon',
		url: 'https://posthog.com',
	},
	{
		name: 'Biome JS',
		icon: 'simple-icons:biome',
		url: 'https://biomejs.dev',
	},
	{
		name: 'Vite',
		icon: 'simple-icons:vite',
		url: 'https://vitejs.dev',
	},
	{
		name: 'Tailwind CSS',
		icon: 'mdi:tailwind',
		url: 'https://tailwindcss.com',
	},
	{
		name: 'Zustand',
		icon: 'devicon-plain:zustand',
		url: 'https://zustand-demo.pmnd.rs/',
	},
	{
		name: 'Zod',
		icon: 'simple-icons:zod',
		url: 'https://zod.dev',
	},
	{
		name: 'TRPC',
		icon: 'devicon-plain:trpc',
		url: 'https://trpc.io',
	},
	{
		name: 'Three JS',
		icon: 'tabler:brand-threejs',
		url: 'https://threejs.org/',
	},
	{
		name: 'Resend',
		icon: 'simple-icons:resend',
		url: 'https://resend.com',
	},
	{
		name: 'Base UI',
		icon: 'simple-icons:baseui',
		url: 'https://baseui.org',
	},
	{
		name: 'Iconify',
		icon: 'simple-icons:iconify',
		url: 'https://iconify.design/',
	},
	{
		name: 'TanStack',
		icon: 'simple-icons:tanstack',
		url: 'https://tanstack.com',
	},
	{
		name: 'Vitest',
		icon: 'devicon-plain:vitest',
		url: 'https://vitest.dev',
	},

	{
		name: 'Vercel',
		icon: 'akar-icons:vercel-fill',
		url: 'https://vercel.com',
	},
	{
		name: 'Sanity',
		icon: 'simple-icons:sanity',
		url: 'https://sanity.io',
	},
] as const;

export function PoweredBy({ className, ...props }: PoweredByProps) {
	return (
		<div className={cn('container', className)} {...props}>
			<div className="space-y-32">
				<Heading
					variant="centered"
					titlePrefix={m.heading_pb_prefix()}
					titleHighlight={m.heading_pb_highlight()}
					description={m.pb_heading_tagline()}
				/>
				<div className="mx-auto flex max-w-xl flex-wrap items-center justify-center gap-8 md:gap-16">
					{TECH_LOGOS.map((tech, idx) => (
						<WithTooltip key={tech.name} content={tech.name} offset={10}>
							<Motion
								delay={idx * 0.1}
								initial={{ opacity: 0, y: 10 }}
								whileInView={{ opacity: 0.5, y: 0 }}
								whileHover={{
									opacity: 1,
									scale: 1.1,
									transition: { delay: 0 },
								}}
								duration={1}
							>
								<a
									href={tech.url}
									target="_blank"
									rel="noopener noreferrer"
									className="filter-[url(#primary-color-filter)] flex shrink-0 items-center justify-center"
								>
									<Iconify icon={tech.icon} className="size-10" />
								</a>
							</Motion>
						</WithTooltip>
					))}
				</div>
			</div>
		</div>
	);
}
